import { IS_MOCKED_SESSION, MS_PER_SECOND } from "@features/session/constants";
import {
	captureErrorToSentry,
	serializeErrorForSentry,
} from "@helpers/sentry-logger";
import { getPlatform } from "@memorang/helpers/platform";
import type { AIResponsePayload, SubskillAssessment } from "@memorang/types";
import { trackAnswerEvent } from "../api/mutations";
import type { TrackAnswerEventRequest } from "../api/types";
import { isPteAssessorSpeakingItem } from "../constants/pte/assessor";
import {
	getAnswerEventRetryDelay,
	shouldRetryAnswerEvent,
} from "../containers/StudySessionBottombarContainer/answer-event-retry";
import type { BlockState } from "../hooks/types";
import useAIFeedbackStore from "../hooks/useAIFeedbackStore";
import useSessionStore from "../hooks/useSessionStore";
import { toFeedbackAiResponse } from "./pte-feedback-response";
import { findSessionItemById, withItemViewedFallback } from "./session";
import { type Sleep, defaultSleep } from "./sleep";

export type PostAiAnswerEventBaseParams = {
	itemId: number;
	rawAnswer: string;
	projectId: number;
	organizationId: number;
	timeTakenSeconds?: number;
};

export type PostAiAnswerEventParams =
	| (PostAiAnswerEventBaseParams & { mode: "PENDING" })
	| (PostAiAnswerEventBaseParams & {
			mode: "COMPLETE";
			feedback: string;
			subskillAssessments: SubskillAssessment[];
			transcription?: string;
			score: number;
			points: number;
			normalizedPoints?: number;
			displayPoints?: number;
	  });

const resolveInteractionFlags = (
	blockStates: Map<number, BlockState>,
	itemId: number,
): { marked: boolean; highlight: string } => {
	for (const blockState of blockStates.values()) {
		const marked = blockState.markedItemsForReview?.includes(itemId) ?? false;
		const highlight = blockState.highlightedItems?.get(itemId);
		if (marked || highlight != null) {
			return { marked, highlight: highlight ?? "" };
		}
	}
	return { marked: false, highlight: "" };
};

const buildAiResponse = (params: PostAiAnswerEventParams): AIResponsePayload =>
	params.mode === "PENDING"
		? {
				feedback: "",
				subskillAssessments: [],
				ai_run_id: "",
				ai_thread_id: "",
				metadata: { assistantId: "" },
				answer: params.rawAnswer,
				status: "PENDING",
			}
		: {
				feedback: params.feedback,
				subskillAssessments: params.subskillAssessments,
				ai_run_id: "",
				ai_thread_id: "",
				metadata: { assistantId: "" },
				answer: params.rawAnswer,
				status: "COMPLETE",
			};

// Only COMPLETE posts with points > 0 are correct; pre-score posts are not.
const resolveCorrectness = (params: PostAiAnswerEventParams) =>
	params.mode === "COMPLETE" && (params.displayPoints ?? params.points) > 0;

export const buildAiAnswerEventRequest = (
	params: PostAiAnswerEventParams,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
): TrackAnswerEventRequest | undefined => {
	const state = useSessionStore.getState();
	const item = findSessionItemById(state.sessionItems, params.itemId);
	const itemType = item?.metadata?.type;
	if (!(item && itemType)) {
		return undefined;
	}

	const sessionEvents = withItemViewedFallback(
		state.sessionEvents?.get(params.itemId),
	);

	const { marked: blockMarked, highlight: blockHighlight } =
		resolveInteractionFlags(state.blockStates, params.itemId);

	const timing =
		params.timeTakenSeconds != null
			? { timeTaken: params.timeTakenSeconds }
			: {};
	const timeSpent =
		params.timeTakenSeconds != null
			? { timeSpentMs: params.timeTakenSeconds * MS_PER_SECOND }
			: {};
	const performance: TrackAnswerEventRequest["performance"] =
		params.mode === "COMPLETE"
			? {
					score: params.score,
					points: params.points,
					...(state.isSummativeTest && params.normalizedPoints !== undefined
						? { normalizedPoints: params.normalizedPoints }
						: {}),
					...(params.displayPoints !== undefined
						? { displayPoints: params.displayPoints }
						: {}),
					xp: 0,
					...timing,
				}
			: { xp: 0, ...timing };

	return {
		itemId: params.itemId,
		itemVersionId: item.versionId || 1,
		sessionId: state.id,
		collectionId: state.collectionId,
		projectId: params.projectId,
		organizationId: params.organizationId,
		sessionType: state.isSummativeTest ? "SUMMATIVE" : "FORMATIVE",
		itemDetails: {
			type: "ANSWER_EVENT",
			itemType,
			variant: itemType,
		},
		response: {
			selectedChoiceIds: [],
			correctness: resolveCorrectness(params),
			userSubmittedAnswer: {
				rawInput: {
					inputType: isPteAssessorSpeakingItem(itemType) ? "audio" : "text",
					value: params.rawAnswer,
				},
			},
		},
		performance,
		settings: {
			shuffled: false,
			numDistractors: 0,
		},
		userInteraction: {
			marked: Boolean(item.answer?.marked) || blockMarked,
			highlight: item.answer?.highlight || blockHighlight,
			sessionTime: state.timeElapsed + (state.sessionTime || 0),
			events: sessionEvents,
			...timeSpent,
		},
		platform: getPlatform(),
		aiResponse: buildAiResponse(params),
	};
};

const postWithRetry = async (
	request: TrackAnswerEventRequest,
	sleep: Sleep,
): Promise<void> => {
	let failureCount = 0;
	for (;;) {
		try {
			await trackAnswerEvent(request);
			return;
		} catch (error) {
			if (!shouldRetryAnswerEvent(failureCount, error)) {
				throw error;
			}
			const delay = getAnswerEventRetryDelay(failureCount, error);
			failureCount += 1;
			await sleep(delay);
		}
	}
};

// Never throws; exhausted retries are logged and surfaced via the boolean return.
export const postAiAnswerEvent = (
	params: PostAiAnswerEventParams,
	sleep: Sleep = defaultSleep,
): Promise<boolean> => {
	const request = buildAiAnswerEventRequest(params);
	if (!request) {
		return Promise.resolve(false);
	}

	return postPrebuiltAnswerEvent(request, sleep);
};

export const postFormativeFeedbackAnswerEvent = async (
	itemId: number,
	feedbackText: string,
): Promise<boolean> => {
	if (IS_MOCKED_SESSION) {
		return false;
	}

	const store = useAIFeedbackStore.getState();
	const stashed = store.getStashedAnswerEventRequest(itemId);
	if (!(stashed && feedbackText)) {
		return false;
	}

	const posted = await postPrebuiltAnswerEvent({
		...stashed,
		// The deterministic post already delivered these events; replaying them
		// would double-count them server-side.
		userInteraction: { ...stashed.userInteraction, events: [] },
		aiResponse: toFeedbackAiResponse({
			feedback: feedbackText,
			answer: stashed.aiResponse?.answer ?? "",
			status: "COMPLETE",
			runDetails: store.aiResponse?.[itemId]?.runDetails,
		}),
	});
	if (posted) {
		store.clearStashedAnswerEventRequest(itemId);
	}
	return posted;
};

// For callers that already hold a fully-built request (e.g. a stashed snapshot
// from an earlier deterministic post) rather than params to build one from.
export const postPrebuiltAnswerEvent = async (
	request: TrackAnswerEventRequest,
	sleep: Sleep = defaultSleep,
): Promise<boolean> => {
	useSessionStore.getState().clearSessionEvents?.(request.itemId);

	try {
		await postWithRetry(request, sleep);
		return true;
	} catch (error) {
		captureErrorToSentry(new Error("Error tracking AI answer event"), {
			error: serializeErrorForSentry(error),
			itemId: request.itemId,
			sessionType: request.sessionType,
		});
		return false;
	}
};
